// Global Instructions Rule Applied!
//
// What deleting a funding round would touch (view__round_delete_impact).
//
// Its own model, not an extension of RoundEntrySchema: that schema is strict
// and is the rounds slice's row type, so widening it to carry counts would
// ripple through every round read. This shape exists only to fill a
// confirmation dialog — the round's headline figures so the user can see WHAT
// is about to go, and the counts of what goes with it.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { TABLE_ROUNDS, FIELD_KEY_SEPARATOR, FIELD_KEY_PROG } from '../globals.ts'
import { RoundsModelFields } from './rounds_model.ts'

const ALIAS_ROUNDS = `${TABLE_ROUNDS}${FIELD_KEY_SEPARATOR}` as const

const FIELD_ROUNDS_ID = `${ALIAS_ROUNDS}${RoundsModelFields.id}` as const
const FIELD_ROUNDS_NAME = `${ALIAS_ROUNDS}${RoundsModelFields.name}` as const
const FIELD_ROUNDS_CURRENCY = `${ALIAS_ROUNDS}${RoundsModelFields.currency}` as const
const FIELD_ROUNDS_RAISE_AMOUNT = `${ALIAS_ROUNDS}${RoundsModelFields.raise_amount}` as const
const FIELD_ROUNDS_PRE_MONEY_VALUATION = `${ALIAS_ROUNDS}${RoundsModelFields.pre_money_valuation}` as const
const FIELD_ROUNDS_POST_MONEY_VALUATION = `${ALIAS_ROUNDS}${RoundsModelFields.post_money_valuation}` as const
const FIELD_ROUNDS_ROUND_CLOSE_DATE = `${ALIAS_ROUNDS}${RoundsModelFields.round_close_date}` as const
const FIELD_ROUNDS_FUNDING_STAGE_LABEL = `${ALIAS_ROUNDS}${RoundsModelFields.funding_stage}__label` as const
const FIELD_ROUNDS_SECURITY_TYPE_LABEL = `${ALIAS_ROUNDS}${RoundsModelFields.security_type}__label` as const

const PROG_CURRENCY_CODE = `${FIELD_KEY_PROG}currency_code` as const
const PROG_INSTRUMENTS = `${FIELD_KEY_PROG}instruments` as const
const PROG_PARTICIPATIONS = `${FIELD_KEY_PROG}participations` as const
const PROG_LINKED_DEALS = `${FIELD_KEY_PROG}linked_deals` as const
const PROG_CAP_TABLE_VERSIONS = `${FIELD_KEY_PROG}cap_table_versions` as const
const PROG_LEGAL_SETS = `${FIELD_KEY_PROG}legal_sets` as const
/** True while a live cap-table version or legal agreement set references the
 *  round — the backend will refuse the delete, so the UI says so up front. */
const PROG_BLOCKED = `${FIELD_KEY_PROG}blocked` as const

export const RoundDeleteImpactSchema = z
  .object({
    [FIELD_ROUNDS_ID]: z.string().nullish(),
    [FIELD_ROUNDS_NAME]: z.string().nullish(),
    [FIELD_ROUNDS_FUNDING_STAGE_LABEL]: z.string().nullish(),
    [FIELD_ROUNDS_SECURITY_TYPE_LABEL]: z.string().nullish(),
    [FIELD_ROUNDS_ROUND_CLOSE_DATE]: z.string().nullish(),
    [FIELD_ROUNDS_RAISE_AMOUNT]: z.number().nullish(),
    [FIELD_ROUNDS_PRE_MONEY_VALUATION]: z.number().nullish(),
    [FIELD_ROUNDS_POST_MONEY_VALUATION]: z.number().nullish(),
    [FIELD_ROUNDS_CURRENCY]: z.string().nullish(),
    [PROG_CURRENCY_CODE]: z.string().nullish(),
    [PROG_INSTRUMENTS]: z.number().nullish(),
    [PROG_PARTICIPATIONS]: z.number().nullish(),
    [PROG_LINKED_DEALS]: z.number().nullish(),
    [PROG_CAP_TABLE_VERSIONS]: z.number().nullish(),
    [PROG_LEGAL_SETS]: z.number().nullish(),
    [PROG_BLOCKED]: z.boolean().nullish()
  })
  .strict()

export const RoundDeleteImpactFields = makeFieldKeys(RoundDeleteImpactSchema)

export type RoundDeleteImpact = z.infer<typeof RoundDeleteImpactSchema>

export function validateRoundDeleteImpact(data: unknown): RoundDeleteImpact {
  try {
    return validateWithZodSchema(RoundDeleteImpactSchema, data, 'round delete impact')
  } catch (error) {
    throw new Error(`Round delete impact validation failed: ${error}`)
  }
}
